/**
 * The Faceling. It only moves while you are not looking at it.
 *
 * This is the scariest thing in the game and the reason is entirely mechanical:
 * the freeze has to be *exact*. A body that coasts five centimetres into the
 * moment the player's eyes land on it is a body the player caught moving, and
 * once they have caught it once the rule is broken for the rest of the run.
 * So the observation test is not run here at all — it runs in the entity
 * system, on the frame, against the same `EntityState.visible` the renderer
 * uses, and it zeroes velocity rather than damping it. This file only declares
 * that the faceling is the sort of thing that freezes (`Agent.freezeWhenSeen`)
 * and then behaves as though it never stopped.
 *
 * What "observed" means, precisely:
 *
 *  - inside the player's view cone (`PLAYER_HALF_FOV`, 55° either side, a
 *    little narrower than the camera so it is already still by the time it is
 *    on screen);
 *  - with a clear line of sight, so a wall between you is not looking at it;
 *  - within `observeRange`, because past the fog nobody is observing anything
 *    and a freeze the player cannot see only slows the game down.
 *
 * And one exception, which is the whole reason it is not merely a nuisance:
 * inside `contactRange` it stops caring. Looking at it does not stop it once it
 * is on you.
 *
 * It makes no sound at all. `ENTITY_TUNING.faceling.silence` exists so that the
 * absence is on purpose and survives the next edit: the effect is that the room
 * is quiet and it is nearer than it was.
 */

import { dist2 } from '../../core/math';
import {
  driveAwareness,
  faceToward,
  holdStill,
  moveTo,
  setMode,
  sweepStep,
  takeHeardNoise,
  tryAttack,
  type Agent,
  type Behavior,
  type EntityWorld,
} from '../brain';
import { ENTITY_TUNING } from '../defs';
import { hearSignal, sightSignal } from '../perception';

const T = ENTITY_TUNING.faceling;

/**
 * Is the player looking at it right now?
 *
 * Reads the system's own answer rather than recomputing one. `visible` is
 * refreshed every frame by `refreshPerception` and is exactly frustum + line of
 * sight; `a.freezeRange` is the faceling's own shorter range, so that being
 * noticed from the far side of a fogged-out atrium does not pin it.
 *
 * The same two numbers the system uses in the same order, deliberately: if this
 * function and the system's freeze test can ever disagree, the disagreement is
 * a body standing still because it is being watched while its own behaviour
 * believes it is walking, which is a stall that lasts the rest of the run.
 */
function observed(a: Agent): boolean {
  return a.state.visible && a.state.distToPlayer <= a.freezeRange;
}

/** Point its memory at a world position, allocating it once. */
function remember(a: Agent, x: number, z: number): void {
  const s = a.state;
  if (!s.lastKnown) s.lastKnown = { x, z };
  else {
    s.lastKnown.x = x;
    s.lastKnown.z = z;
  }
}

export const facelingBehavior: Behavior = {
  kind: 'faceling',

  spawn(a, w) {
    // The declaration. Everything else about the freeze is the system's job.
    a.freezeRange = T.observeRange;
    // Slow. When it is allowed to turn at all it should look like something
    // that does not have joints where a person has them.
    a.turnRate = 1.6;
    a.timer2 = 0;
    setMode(a, w, 'dormant');
  },

  think(a, w) {
    const s = a.state;

    const signal = Math.max(
      sightSignal(a.def, s, w.grid, w.player, w.playerLight),
      hearSignal(a.def, s, w.grid, w.player),
    );
    // A player who can see it can be seen by it: mutual line of sight is the
    // same line. So being frozen never blinds it, and standing there staring
    // does not make it forget where you are.
    const mutual = observed(a) ? 1 : 0;
    driveAwareness(a, w, Math.max(signal, mutual), T.gain, T.forget);

    const heard = takeHeardNoise(a);
    if (heard) {
      remember(a, heard.x, heard.z);
      s.awareness = Math.min(1, s.awareness + 0.4);
      a.searchTimer = T.patience;
    }
    if (s.awareness > 0) a.searchTimer = Math.max(a.searchTimer, T.patience * s.awareness);

    // Contact. It attacks whether or not it is being watched — this is the one
    // thing that stops a player solving it by staring, and it is why the
    // approach has to be worth being afraid of.
    //
    // The threshold is `attackRange` and not a hair more, because it is also
    // the distance the system stops holding the body at. A behaviour that
    // stopped moving a little further out than the freeze released would leave
    // it parked just outside its own reach: measured, 0 of 32 spawns ever
    // landed a hit, with a median closest approach of 1.7 m against an attack
    // range of 1.5.
    if (s.distToPlayer <= a.def.attackRange) {
      setMode(a, w, 'attack');
      faceToward(a, w.player.pos);
      holdStill(a);
      if (tryAttack(a, w)) {
        s.awareness = 1;
        remember(a, w.player.pos.x, w.player.pos.z);
      }
      return;
    }

    if (observed(a)) {
      // Held. `holdStill` is belt and braces — the system has already zeroed
      // the body this frame — but it keeps the goal from being stale by the
      // time the player looks away, and it stops the dread reading as a chase.
      holdStill(a);
      setMode(a, w, 'stalk');
      // Looking at it is what keeps it still, and looking at it is what costs.
      // Flat with distance rather than falling off, unlike the smiler: the
      // smiler punishes walking toward it, and this punishes not daring to
      // look away.
      w.dread(s, a.def.sanityDrain);
      return;
    }

    // Unobserved. It closes, in silence, at full speed.
    if (s.lastKnown && s.awareness > 0.05) {
      setMode(a, w, 'chase');
      const target = dist2(s.pos, w.player.pos) < dist2(s.pos, s.lastKnown) ? w.player.pos : s.lastKnown;
      moveTo(a, w, target, a.def.chaseSpeed);
      // Nothing keeps it pointed at the player while it walks; `moveTo` faces
      // it along its route, which for a thing with no face is the right answer
      // and means the moment it *is* facing you is a moment it chose.
      if (a.searchTimer <= 0) {
        s.lastKnown = null;
        s.awareness = 0;
      }
      return;
    }

    // It has genuinely lost them, after half a minute of not losing them.
    // Sweep for the player like anything else that hunts — a noisily-walking
    // player gets found, a crouched still one does not.
    //
    // `wander` and not `investigate`, which would read as the same thing: the
    // director treats investigate as an encounter in progress and holds its
    // pressure back for as long as one lasts, so a faceling that never left
    // that mode would quietly switch the rest of the level off.
    setMode(a, w, 'wander');
    sweepStep(a, w, a.def.walkSpeed);
    if (s.awareness > 0.2 && s.lastKnown) setMode(a, w, 'chase');
  },
};
